import { useState } from "react"
import { DateRangePicker } from "rsuite"
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Button,
} from "@mui/material"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import Chart from "@/components/charts/Chart"
import DataTable from "@/components/Tables/DataTable"
import { columns } from "./columns"
import "rsuite/dist/rsuite.min.css"
import { exportReport } from "@/hooks/useReportExport"
import { BrandSelect } from "../../Brand/BrandSelect"
import { useAppSelector } from "@/lib/hooks"
import SalesOrderTypeSelect from "../SalesOrderTypeSelect"
import { useReportBranch } from "@/components/Reports/ReportBranchFilter"

const MonthlyReport = ({ orderType, onOrderTypeChange }) => {
  const currentYear = new Date().getFullYear()
  const [dateRange, setDateRange] = useState([
    new Date(`${currentYear}-01-01`), // Start of current year
    new Date(), // Today
  ])
  const selectedBrand = useAppSelector((state) => state.brand.selectedBrandId)
  const { branchId } = useReportBranch()
  const { data, isLoading } = useCustomQuery(
    [
      "monthly-sales-report",
      dateRange[0],
      dateRange[1],
      selectedBrand,
      orderType,
      branchId,
    ],
    `report/sales/monthly?start_date=${dateRange[0].toISOString()}&end_date=${dateRange[1].toISOString()}&brand_id=${selectedBrand}&order_type=${orderType}&branch_id=${branchId ?? ""}`
  )

  //export
  const handleExport = async () => {
    return exportReport({
      startDate: dateRange[0].toISOString(),
      endDate: dateRange[1].toISOString(),
      brandId: selectedBrand,
      orderType,
      branchId,
      reportType: "monthly-sales",
    })
  }

  const reversedData = data ? [...data].reverse() : []

  // Prepare chart data
  const chartData = {
    labels: [],
    salesData: [],
    ordersData: [],
  }

  if (data) {
    data.forEach((item) => {
      const monthName = new Date(item.sale_month + "-01").toLocaleDateString(
        "en-US",
        { month: "short", year: "numeric" }
      )
      chartData.labels.push(monthName)
      chartData.salesData.push(item.total_sales)
      chartData.ordersData.push(item.total_orders)
    })
  }

  // Calculate summary stats
  const summaryStats = data
    ? {
        totalSales: data.reduce(
          (sum, item) => sum + parseFloat(item.total_sales),
          0
        ),
        totalOrders: data.reduce(
          (sum, item) => sum + parseInt(item.total_orders),
          0
        ),
        averageSales:
          data.reduce((sum, item) => sum + parseFloat(item.total_sales), 0) /
          data.length,
        averageOrders:
          data.reduce((sum, item) => sum + parseInt(item.total_orders), 0) /
          data.length,
      }
    : null

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Monthly Sales Report
      </Typography>

      {/* Date Range Picker Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Date Range
          </Typography>

          <div className="flex flex-wrap items-center gap-2">
            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              format="MMM yyyy"
              placeholder="Select date range"
              cleanable={false}
              style={{ width: 300 }}
              appearance="default"
              hoverRange="month"
              defaultCalendarValue={[
                new Date(`${currentYear}-01-01`),
                new Date(`${currentYear}-12-31`),
              ]}
            />
            <SalesOrderTypeSelect
              value={orderType}
              onChange={onOrderTypeChange}
            />
            <div className="flex-1">
              <BrandSelect />
            </div>
            <Button
              variant="outlined"
              onClick={() => handleExport()}
              sx={{
                textTransform: "capitalize",
                borderRadius: "7px",
                fontWeight: "500",
                fontSize: "13px",
                padding: "6px 3px",
              }}
              color="primary"
            >
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summaryStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Sales</Typography>
              <Typography variant="h5" color="primary">
                Rs.{" "}
                {summaryStats.totalSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Total Orders</Typography>
              <Typography variant="h5" color="secondary">
                {summaryStats.totalOrders.toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Avg. Monthly Sales</Typography>
              <Typography variant="h5">
                Rs.{" "}
                {summaryStats.averageSales.toLocaleString("en-IN", {
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper elevation={3} sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="subtitle1">Avg. Monthly Orders</Typography>
              <Typography variant="h5">
                {Math.round(summaryStats.averageOrders).toLocaleString()}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Sales
              </Typography>
              <Chart
                type="line"
                data={chartData.salesData}
                labels={chartData.labels}
                title="Monthly Sales"
                backgroundColor="rgba(63, 81, 181, 0.5)"
                borderColor="#3f51b5"
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Orders
              </Typography>
              <Chart
                type="bar"
                data={chartData.ordersData}
                labels={chartData.labels}
                title="Monthly Orders"
                backgroundColor="rgba(76, 175, 80, 0.5)"
                borderColor="#4caf50"
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Data Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Monthly Data
          </Typography>
          <DataTable
            columns={columns}
            data={reversedData}
            isLoading={isLoading}
            sx={{ mt: 2 }}
          />
        </CardContent>
      </Card>
    </Box>
  )
}

export default MonthlyReport
